/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import {lruMemoize} from 'reselect';

// import lodash functions individually to minimize bundle size
import isFunction from 'lodash/isFunction';
import isObject from 'lodash/isObject';
import {emptyArray} from '@packages/utilities/fp/utils';

/*
 * Inlined Immutable v3.8.1 `is` to reduce webflow.js bundle size
 * Source: https://github.com/immutable-js/immutable-js/blob/v3.8.1/src/is.js
 */
const is = (valueA: any, valueB: any): boolean => {
  // eslint-disable-next-line no-self-compare
  if (valueA === valueB || (valueA !== valueA && valueB !== valueB)) {
    return true;
  }
  if (!valueA || !valueB) {
    return false;
  }
  if (
    typeof valueA.valueOf === 'function' &&
    typeof valueB.valueOf === 'function'
  ) {
    valueA = valueA.valueOf();
    valueB = valueB.valueOf();
    // eslint-disable-next-line no-self-compare
    if (valueA === valueB || (valueA !== valueA && valueB !== valueB)) {
      return true;
    }
    if (!valueA || !valueB) {
      return false;
    }
  }
  if (
    typeof valueA.equals === 'function' &&
    typeof valueB.equals === 'function' &&
    valueA.equals(valueB)
  ) {
    return true;
  }
  return false;
};

export const isEqual = (a: any, b: any): boolean => {
  if (is(a, b)) {
    return true;
  }

  if (
    typeof a !== 'object' ||
    a === null ||
    typeof b !== 'object' ||
    b === null
  ) {
    return false;
  }

  for (const k in a) {
    if (!is(a[k], b[k])) {
      return false;
    }
  }

  return Object.keys(a).length === Object.keys(b).length;
};

/**
 * Memoizes a function with an LRU cache.
 *
 * Usually `lruMemoize` caches by reference, but we use `isEqual` to deeply compare arguments,
 * so do not use this to memoize functions whose arguments are large objects or arrays.
 *
 * `maxSize` makes reference to the number of results to cache: default is 1, which means that
 * only the most recent result is cached. If you expect to use this method to cache multiple results,
 * like for example elements in the canvas, or arguments you'd expect to change reasonably often,
 * you should set `maxSize` to a higher value.
 *
 * @param fn - The function to memoize.
 * @param maxSize - The maximum number of results to cache.
 * @returns
 */
export function memoize<F extends Function>(fn: F, maxSize: number = 1): F {
  // We need to cast `fn` to some ungodly type because `lruMemoize` expects
  // (...args: any[]) => any, but we want to be more strict about the type of `fn`.
  // In other words, if we accept the same type than `lruMemoize` does, we
  // stop requesting fn to be properly typed (as it infers that both arguments
  // and return type are already `any`). For further discussion about this:
  // https://github.com/webflow/webflow/pull/85516/files#r2128406623
  return lruMemoize(fn as F & ((...args: any[]) => any), {
    maxSize,
    equalityCheck: isEqual,
  });
}

/*
 * WARNING: `weakMemo` does not throw errors or handle invalid keys gracefully
 * on purpose. This is a case where we want the app to crash if an invalid key
 * is used. The purpose of allowing the app to crash in this situation is to
 * collect data so a root cause might be discovered and addressed.
 *
 * WARNING: `weakMemo` uses only the first fn's argument as a key. 2nd and further arguments are ignored.
 */
export function weakMemo<F extends Function>(fn: F): F {
  if (process.env.NODE_ENV === 'development') {
    if (!isFunction(fn)) {
      console.error(
        `Expected a function as argument to weakMemo but got ${fn}.`
      );
    }
  }

  const map = new WeakMap();

  // @ts-expect-error - TS2322 - Type '{ (arg: any): any; displayName: string; }' is not assignable to type 'F'. | TS7006 - Parameter 'arg' implicitly has an 'any' type.
  const memFn: F = (arg) => {
    if (!isObject(arg)) {
      const errorMessage = `Expected an object as an argument to "${
        // @ts-expect-error - TS2339 - Property 'displayName' does not exist on type 'F'.
        fn.displayName || fn.name
      }()", but received ${String(arg)}.`;

      if (process.env.NODE_ENV === 'development') {
        const fnString = fn.toString();
        console.info(
          `\x1b[1m\x1b[32mFunction Source:\x1b[0m\n\n` +
            `\x1b[33mweakMemo\x1b[0m(${syntaxHighlight(fnString)})`
        );
      }

      throw new WeakMemoError({
        message: errorMessage,
        memFn,
      });
    }

    if (!map.has(arg)) {
      map.set(arg, fn(arg));
    }

    const result = map.get(arg);

    return result;
  };

  if (process.env.NODE_ENV === 'development') {
    // @ts-expect-error - TS2339 - Property 'displayName' does not exist on type 'F'.
    memFn.displayName =
      // @ts-expect-error - TS2339 - Property 'displayName' does not exist on type 'F'. | TS2339 - Property 'name' does not exist on type 'F'. | TS2339 - Property 'toString' does not exist on type 'F'.
      `weakMemo(\n  ${fn.displayName || fn.name || fn.toString()}\n)`;

    memFn.toString = function () {
      return `weakMemo(\n  ${fn.toString()}\n)`;
    };
  }

  return memFn;
}

function getFnName(fn: Function): string {
  // @ts-expect-error - TS2339 - Property 'displayName' does not exist on type 'Function'.
  return fn.displayName || fn.name || 'unknown';
}

export function weakMemoMulti<ExpensiveFn extends (...args: Array<any>) => any>(
  // the arguments can only be objects. this can't be expressed easily
  // in the `extends` type above, so instead we constrain the type
  // of the function passed in here

  // since weakMemoMulti is only performant when used to memoise functions
  // with at least four arguments, we use the type system to enforce that
  // the function to be memoised does have at least four arguments
  fn: Parameters<ExpensiveFn> extends readonly [
    object,
    object,
    object,
    object,
    ...object[],
  ]
    ? ExpensiveFn
    : never
): ExpensiveFn {
  if (process.env.NODE_ENV === 'development' && !isFunction(fn)) {
    throw new Error(
      `Expected a function as argument to weakMemoMulti but got ${fn}.`
    );
  }

  const argsLength = fn.length;

  // if the function takes less than four arguments, we should use a different approach.
  // this will also catch any functions which use rest parameters.
  if (argsLength <= 3) {
    throw new Error(
      `weakMemoMulti: cannot be used to memoize a function which has less than four arguments, or which uses rest parameters.`
    );
  }

  // for functions with arity of 4 or higher, we use a tree-based algorithm to
  // memoize the function. it starts with a single WeakMap at the root, and
  // dynamically creates a new WeakMap for each level of the tree when an
  // argument is not found in the current level. this allows it to work
  // for any arbitrary number of arguments!
  const rootLevel = new WeakMap();

  const memFn: ExpensiveFn = ((...args) => {
    if (args.length !== argsLength) {
      throw new Error(
        `weakMemoMulti: Expected ${argsLength} arguments to ${getFnName(memFn)}, but received ${args.length}.`
      );
    }

    let currentLevel = rootLevel;

    for (let i = 0; i < argsLength; i++) {
      const key = args[i];

      if (!isObject(key)) {
        throw new TypeError(
          `weakMemoMulti: Expected an object as an argument to ${getFnName(
            memFn
          )} but got ${key === null ? 'null' : typeof key} at index ${i}.`
        );
      }

      if (!currentLevel.has(key)) {
        currentLevel.set(key, i < argsLength - 1 ? new WeakMap() : fn(...args));
      }

      currentLevel = currentLevel.get(key);
    }

    return currentLevel;
  }) as ExpensiveFn;

  if (
    process.env.NODE_ENV === 'development' ||
    process.env.NODE_ENV === 'test'
  ) {
    // @ts-expect-error - TS2339 - Property 'displayName' does not exist on type 'F'.
    memFn.displayName = `weakMemoMulti(${getFnName(fn)})`;
  }

  return memFn;
}

/**
 * Creates a memoize factory with a cache size of the given depth.
 * Only functions that accept a single argument are valid.
 *
 * ⚠️IMPORTANT⚠️ Object and Function arguments are compared *by reference*.
 *
 * @param  {number} depth         The size of the cache.
 * @return {Function}             The resulting memoize function.
 */
export const cacheMemo = (depth: number) => {
  /**
   * Returns a memoized version of a "trivially hashable" function passed as parameter, with a fixed cache size.
   *
   * A "trivially hashable" function takes a single string as an argument and therefore does not need to be hashed.
   *
   * This is a pretty significant optimization if your function needs to be called very frequently.
   *
   * The returned function is memoized via an LRU cache. This means that the least recently used key will
   * be the first one to be evicted when the cache is full (cache size defined by the `depth` parameter).
   * To do this, we are using to interlinked data structures: a `Map` to store the key-value pairs and a linked
   * list to track the order of the keys (so we know which keys are the most and least recently used and what
   * to evict when the cache is full).
   *
   * The reason we are not using `lru-cache` library (which is a popular LRU cache implementation and used elsewhere
   * in the codebase) is because, in order to increase its performance, it pre-allocates five fixed size arrays of size
   * `depth`when initialized. This practice lead to a problem where, in some scenarios, we were creating five array
   * of 10,000 elements each, which was a significant memory overhead, specially considering our heavy memoization
   * and patterns like the undo/redo store (which keeps references to whole state quite often).
   *
   * This homebrew implementation is not as performant as `lru-cache` library, but it is more memory efficient and
   * does not pre-allocate any fixed size arrays.
   *
   * @param  {Function} fn          The function to memoize.
   * @return {Function}             The memoized function.
   */
  const memoizeFn = <F extends Function>(fn: F): F => {
    const maxDepth = depth || 1;
    if (!Number.isInteger(maxDepth) || maxDepth < 0) {
      throw new TypeError('max option must be a nonnegative integer');
    }

    interface Node {
      key: unknown;
      value: unknown;
      prev: Node | null;
      next: Node | null;
    }

    const index = new Map<unknown, Node>();
    let head: Node | null = null;
    let tail: Node | null = null;

    /**
     * Removes a node from the linked list structure.
     */
    const detach = (node: Node) => {
      if (node.prev) {
        node.prev.next = node.next;
      } else {
        head = node.next;
      }
      if (node.next) {
        node.next.prev = node.prev;
      } else {
        tail = node.prev;
      }
      node.prev = null;
      node.next = null;
    };

    /**
     * Adds a node to the head of the linked list structure.
     */
    const prepend = (node: Node) => {
      node.next = head;
      node.prev = null;
      if (head) {
        head.prev = node;
      }

      head = node;

      if (!tail) {
        tail = node;
      }
    };

    // @ts-expect-error - TS2322 - Type '(arg: F) => unknown' is not assignable to type 'F'.
    return function (arg: F) {
      // Nodes are always objects; undefined means cache miss.
      const existing = index.get(arg);
      if (existing !== undefined) {
        detach(existing);
        prepend(existing);
        return existing.value;
      }

      // fn is called before any list mutation so re-entrant calls
      // complete their own insert/evict cycle first.
      const result = fn(arg);

      if (result === undefined) {
        return result;
      }

      const node: Node = {key: arg, value: result, prev: null, next: null};
      index.set(arg, node);
      prepend(node);

      if (index.size > maxDepth && tail) {
        const evicted = tail;
        detach(evicted);
        index.delete(evicted.key);
      }

      return result;
    };
  };

  return memoizeFn;
};

/**
 * A simple memoization function for usage with primitives or single argument
 * arguments of identity equality.
 */
const defaultLastArg = Symbol();
export const singleMemo = <F extends Function>(fn: F): F => {
  let lastArg = defaultLastArg;
  // @ts-expect-error - TS7034 - Variable 'lastResult' implicitly has type 'any' in some locations where its type cannot be determined.
  let lastResult;
  // @ts-expect-error - TS2322 - Type '(arg: F) => any' is not assignable to type 'F'.
  return (arg: F) => {
    // @ts-expect-error - TS2367 - This condition will always return 'true' since the types 'F' and 'symbol' have no overlap.
    if (arg !== lastArg) {
      lastResult = fn(arg);
      // @ts-expect-error - TS2322 - Type 'F' is not assignable to type 'symbol'.
      lastArg = arg;
    }
    // @ts-expect-error - TS7005 - Variable 'lastResult' implicitly has an 'any' type.
    return lastResult;
  };
};

/**
 * `singleMemoMulti` should be used when you want to perform shallow reference comparisons and only retain the
 * most recently invoked memoized parameters.
 */
export const singleMemoMulti = <F extends (...args: any) => any>(fn: F) => {
  let lastArgs = emptyArray;
  let lastResult: ReturnType<F>;

  if (fn.length <= 3) {
    throw new Error(
      `singleMemoMulti: cannot be used to memoize a function which has less than four arguments, manually nest singleMemo instead`
    );
  }

  // Using rest and spread rather than arguments.apply after running this benchmark: https://jsperf.app/lalevi
  return (...args: Parameters<F>): ReturnType<F> => {
    if (lastArgs === emptyArray) {
      lastArgs = args;
      lastResult = fn(...args);
      return lastResult;
    }
    for (let i = 0; i < args.length; i++) {
      if (lastArgs[i] !== args[i]) {
        lastResult = fn(...args);
        lastArgs = args;
        return lastResult;
      }
    }
    return lastResult!;
  };
};

/**
 * A memoization function that only calls the provided nullary function
 * the first time. Subsequent calls return the first result.
 */
export const once = <a>(fn: (arg1: void) => a): ((arg1: void) => a) => {
  let result: a;
  return () => {
    if (fn) {
      result = fn();
      // @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type '(arg1: undefined) => a'.
      fn = undefined;
    }
    return result;
  };
};

class WeakMemoError extends TypeError {
  constructor(opts: {message: string; memFn: Function}) {
    super();

    if (TypeError.captureStackTrace) {
      TypeError.captureStackTrace(this, opts.memFn);
    }

    this.name = 'WeakMemoError';
    this.message = opts.message;
  }
}

function syntaxHighlight(fnSource: string) {
  const keywords =
    /\b(function|return|const|let|if|else|throw|for|while|switch|case|break|continue|default|new|delete|typeof|instanceof|void|this|class)\b/g;
  const literals = /\b(true|false|null|undefined)\b/g;
  const strings = /(['"`][^'"`]*['"`])/g; // Matches single, double, or backticks (template literals)
  const numbers = /\b(\d+(\.\d+)?(e[+-]?\d+)?|NaN|Infinity)\b/gi; // Matches integers, floats, and scientific notation
  const operators = /[=+\-*\/%<>!&|^~?]/g;
  const comments = /(\/\/[^\n]*|\/\*[\s\S]*?\*\/)/g;
  const parentheses = /[\(\)\[\]\{\}]/g; // Matches (), [], {}

  // Apply syntax highlighting via ANSI escape codes
  return fnSource
    .replace(parentheses, (match) => `\x1b[37m${match}\x1b[0m`) // white for parentheses and brackets
    .replace(comments, (match) => `\x1b[90m${match}\x1b[0m`) // gray for comments
    .replace(strings, (match) => `\x1b[32m${match}\x1b[0m`) // green for strings and template literals
    .replace(literals, (match) => `\x1b[33m${match}\x1b[0m`) // yellow for literals (true, false, null, undefined)
    .replace(keywords, (match) => `\x1b[35m${match}\x1b[0m`) // magenta for keywords
    .replace(numbers, (match) => `\x1b[36m${match}\x1b[0m`) // cyan for numbers
    .replace(operators, (match) => `\x1b[31m${match}\x1b[0m`); // red for operators
}
